"use client";

import { devLabel } from "@/lib/dev-labels";
import type { Wizard } from "./wizards-data";
import styles from "./MagicWandModal.module.css";

/**
 * WizardCard — Phase 25-AB Task 14.
 *
 * Mockup source: docs/mockups/magic-wand.html (panel 28 — every .ai-card).
 *
 * One card per AI wizard. The icon slot shows a 3-char monospace
 * abbreviation derived from the wizard key (no SVG / emoji, per
 * D-2202 / D-2204). Clicking either the card body or the inline
 * "실행 ▸" button fires `onRun(wizard.key)`.
 */

export interface WizardCardProps {
	wizard: Wizard;
	onRun: (key: string) => void;
	devLabelId?: string;
}

function costClass(coinCost: number): string {
	if (coinCost === 0) return `${styles.aiCost} ${styles.aiCostFree}`;
	if (coinCost >= 5) return `${styles.aiCost} ${styles.aiCostHigh}`;
	return styles.aiCost;
}

export function WizardCard({ wizard, onRun, devLabelId }: WizardCardProps) {
	function handleRun(e: React.MouseEvent | React.KeyboardEvent) {
		e.stopPropagation();
		onRun(wizard.key);
	}
	const cardClass = [
		styles.aiCard,
		wizard.featured ? styles.featured : "",
	]
		.filter(Boolean)
		.join(" ");

	// Derive a 2-letter monospace icon from the wizard key suffix.
	// AI-01 → "01", AI-29 → "29". Stable and zero-asset.
	const ico = wizard.key.replace(/^AI-/, "");

	return (
		<article
			data-testid={`wizard-${wizard.key}`}
			data-wizard-key={wizard.key}
			className={cardClass}
			role="button"
			tabIndex={0}
			onClick={handleRun}
			onKeyDown={(e) => {
				if (e.key === "Enter" || e.key === " ") {
					e.preventDefault();
					handleRun(e);
				}
			}}
			aria-label={`${wizard.name} 실행 (${wizard.coinLabel})`}
			{...(devLabelId ? devLabel(devLabelId) : {})}
		>
			{wizard.isNew && (
				<span className={styles.badgeNew} aria-label="신규">
					NEW
				</span>
			)}
			<span className={styles.aiNum}>{wizard.key}</span>
			<div className={styles.aiCardHead}>
				<div className={styles.aiIco} aria-hidden="true">
					{ico}
				</div>
				<div>
					<div className={styles.aiName}>{wizard.name}</div>
					<div className={styles.aiCatTag}>{wizard.tag}</div>
				</div>
			</div>
			<p className={styles.aiDesc}>{wizard.description}</p>
			<div className={styles.aiFoot}>
				<span className={costClass(wizard.coinCost)}>{wizard.coinLabel}</span>
				<button
					type="button"
					className={styles.aiRun}
					onClick={handleRun}
					data-testid={`wizard-run-${wizard.key}`}
					aria-label={`${wizard.name} 실행`}
				>
					실행 ▸
				</button>
			</div>
		</article>
	);
}
